<script setup lang="ts">
import { ref, computed } from 'vue';
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EllipsisHorizontalIcon,
  MapPinIcon,
  InformationCircleIcon
} from '@heroicons/vue/20/solid'
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import Calendar from '@/components/Calendar.vue';
import { AttributeConfig } from "@/interfaces/VCalendar";


const trials = [
  {
    id: 1,
    date: new Date(2023, 4, 11, 10),
    name: 'Maître Jérémie Le Douche',
    imageUrl:
      'https://ucarecdn.com/3cfaa896-03fb-46a5-a868-dfb2ec4e34ef/',
    location: 'Tribunal de Commerce de Créteil',
    trialId: '2023F00182',
  },
  {
    id: 2,
    date: new Date(2023, 4, 11, 10),
    name: 'Maître Régis Pihery',
    imageUrl:
      'https://ucarecdn.com/f3e2cef1-868c-403a-b20b-244d47e886c8/regis1.jpeg',
    location: 'Tribunal de Commerce de Créteil',
    trialId: '2023F00183',
  },
  // More trials...
];

const selectAttribute = ref<AttributeConfig>({ dot: true });
const attributes: AttributeConfig[] = [
  // This is a single attribute
  {
    // An optional key can be used for retrieving this attribute later,
    // and will most likely be derived from your data object
    // Attribute type definitions
    // content: 'red',   // Boolean, String, Object
    // highlight: true,  // Boolean, String, Object
    dot: true,        // Boolean, String, Object
    // bar: true,        // Boolean, String, Object
    popover: {
      label: "Audience Creteil Transavia",
      isInteractive: true
    }, // Only objects allowed
    // Your custom data object for later access, if needed
    // We also need some dates to know where to display the attribute
    // We use a single date here, but it could also be an array of dates,
    //  a date range or a complex date pattern.
    dates: new Date(),
  },
  {
    // Boolean
    bar: true,
    dates: [new Date(2023, 4, 1), new Date(2023, 4, 9), new Date(2023, 4, 22)],
  },
  {
    // String
    bar: 'red',
    dates: [new Date(2018, 4, 4), new Date(2023, 4, 9), new Date(2023, 4, 15)],
  },
];
const date = ref(new Date())
function page({ to }) {
  date.value = to;
}
</script>
<template>
  <div class="w-full p-12">
  <h2 class="text-lg font-semibold leading-6 text-left text-gray-900">
    Audiences à venir - <span class="capitalize">{{ format(new Date(), 'MMMM yyyy', { locale: fr }) }}</span>
  </h2>

  <div class="lg:grid lg:grid-cols-12 lg:gap-x-16">
    <div class="text-center lg:col-start-8 lg:col-end-13 lg:row-start-1 xl:col-start-9">
      <Calendar />
    </div>

      <ol class="mt-4 text-sm leading-6 divide-y divide-gray-100 lg:col-span-7 xl:col-span-8">
        <!-- 🚧 Temporary banner -->
        <div class="p-4 mb-6 rounded-md bg-blue-50 lg:col-span-7 xl:col-span-8">
          <div class="flex">
            <div class="flex-shrink-0">
              <InformationCircleIcon class="w-5 h-5 text-blue-400" aria-hidden="true" />
            </div>
            <div class="flex-1 ml-3 md:flex md:justify-between">
              <p class="text-sm text-blue-700">Une prochaine mise à jour connectera bientôt les audiences, directement sur cette page.</p>
            </div>
          </div>
        </div>
        <li v-for="trial in trials" :key="trial.id" class="relative flex py-6 space-x-6 xl:static">
            <img :src="trial.imageUrl" alt="Avocat" class="flex-none rounded-full h-14 w-14" />
            <div class="flex-auto">
              <h3 class="pr-10 font-semibold text-left text-gray-900 xl:pr-0">{{ trial.name }}</h3>
              <dl class="flex flex-col mt-2 text-gray-500 xl:flex-row">
                <div class="flex items-start space-x-3">
                  <dt class="mt-0.5">
                    <span class="sr-only">Date</span>
                    <CalendarIcon class="w-5 h-5 text-gray-400" aria-hidden="true" />
                  </dt>
                  <dd>
                    <time :datetime="trial.date.toISOString()">
                      {{ format(trial.date, "d MMMM yyyy 'à' HH':'mm", { locale: fr }) }}
                    </time>
                  </dd>
                </div>
                <div
                  class="mt-2 flex items-start space-x-3 xl:ml-3.5 xl:mt-0 xl:border-l xl:border-gray-400 xl:border-opacity-50 xl:pl-3.5">
                  <dt class="mt-0.5">
                    <span class="sr-only">Location</span>
                    <MapPinIcon class="w-5 h-5 text-gray-400" aria-hidden="true" />
                  </dt>
                  <dd>{{ trial.location }}</dd>
                </div>
                <div class="mt-2 flex items-start space-x-3 xl:ml-3.5 xl:mt-0 xl:border-l xl:border-gray-400 xl:border-opacity-50 xl:pl-3.5">
                  <p class="">N° RG: {{ trial.trialId }}</p>
                </div>
              </dl>
            </div>
            <Menu as="div" class="absolute right-0 top-6 xl:relative xl:right-auto xl:top-auto xl:self-center">
              <div>
                <MenuButton class="flex items-center p-2 -m-2 text-gray-500 rounded-full hover:text-gray-600">
                  <span class="sr-only">Open options</span>
                  <EllipsisHorizontalIcon class="w-5 h-5" aria-hidden="true" />
                </MenuButton>
              </div>

              <transition enter-active-class="transition duration-100 ease-out"
                enter-from-class="transform scale-95 opacity-0" enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-75 ease-in" leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0">
                <MenuItems
                  class="absolute right-0 z-10 mt-2 text-left origin-top-right bg-white rounded-md shadow-lg w-36 ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <div class="py-1">
                    <MenuItem v-slot="{ active }">
                    <a href="#"
                      :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'block px-4 py-2 text-sm']">Modifier</a>
                    </MenuItem>
                    <MenuItem v-slot="{ active }">
                    <a href="#"
                      :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'block px-4 py-2 text-sm']">Voir la claim</a>
                    </MenuItem>
                  </div>
                </MenuItems>
              </transition>
            </Menu>
        </li>
      </ol>
    </div>
  </div>
</template>